import React, { createContext, useContext, useState, useCallback } from 'react';
import { DifficultySettings, GameMetrics, RoundData, DDAState } from './types';

interface MatchingGridContextType {
  currentSettings: DifficultySettings;
  metrics: GameMetrics;
  roundHistory: RoundData[];
  updateSettings: (settings: Partial<DifficultySettings>) => void;
  saveRoundData: (data: RoundData) => void;
  resetGame: () => void;
  ddaState: DDAState;
  updateDDAState: (state: Partial<DDAState>) => void;
}

const initialSettings: DifficultySettings = {
  gridSize: { rows: 2, cols: 2 },  // Start with very easy 2x2 grid (4 cards, 2 pairs)
  flipBackDelay: 1500,             // More time to see cards (easier)
  previewTime: 4000,              // Longer preview (easier)
  hintsEnabled: true,              // Start with hints enabled (easier)
  imageSimilarityLevel: 1,        // Easy images at start
};

const initialMetrics: GameMetrics = {
  correctPairs: 0,
  totalAttempts: 0,
  errors: 0,
  totalTime: 0,
  averageReactionTime: 0,
  accuracy: 0,
};

const initialDDAState: DDAState = {
  consecutivePromotions: 0,
  consecutiveReliefs: 0,
  reliefsInLastFiveRounds: 0,
  lastRoundWasRelief: false,
  roundsSinceLastPromotion: 0,
};

const MatchingGridContext = createContext<MatchingGridContextType | undefined>(undefined);

export const MatchingGridProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentSettings, setCurrentSettings] = useState<DifficultySettings>(initialSettings);
  const [metrics, setMetrics] = useState<GameMetrics>(initialMetrics);
  const [roundHistory, setRoundHistory] = useState<RoundData[]>([]);
  const [ddaState, setDDAState] = useState<DDAState>(initialDDAState);

  const updateSettings = useCallback((newSettings: Partial<DifficultySettings>) => {
    setCurrentSettings(prev => ({
      ...prev,
      ...newSettings,
    }));
  }, []);

  const saveRoundData = useCallback((data: RoundData) => {
    setRoundHistory(prev => [...prev, data]);
    setMetrics(prev => ({
      ...prev,
      correctPairs: prev.correctPairs + data.correctPairs,
      totalAttempts: prev.totalAttempts + data.totalAttempts,
      errors: prev.errors + data.errors,
      totalTime: prev.totalTime + data.totalTime,
      averageReactionTime: 
        (prev.averageReactionTime * prev.totalAttempts + data.averageReactionTime * data.totalAttempts) / 
        (prev.totalAttempts + data.totalAttempts),
      accuracy: (prev.correctPairs + data.correctPairs) / (prev.totalAttempts + data.totalAttempts),
    }));
  }, []);

  const updateDDAState = useCallback((newState: Partial<DDAState>) => {
    setDDAState(prev => ({
      ...prev,
      ...newState,
    }));
  }, []);

  const resetGame = useCallback(() => {
    setCurrentSettings(initialSettings);
    setMetrics(initialMetrics);
    setRoundHistory([]);
    setDDAState(initialDDAState);
  }, []);

  return (
    <MatchingGridContext.Provider
      value={{
        currentSettings,
        metrics,
        roundHistory,
        updateSettings,
        saveRoundData,
        resetGame,
        ddaState,
        updateDDAState,
      }}
    >
      {children}
    </MatchingGridContext.Provider>
  );
};

export const useMatchingGrid = () => {
  const context = useContext(MatchingGridContext);
  if (!context) {
    throw new Error('useMatchingGrid must be used within a MatchingGridProvider');
  }
  return context;
};